import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TimeoutError } from 'rxjs';
import {
  CREDENTIAL_VALIDATION_FAILED,
  ERROR_MSG_SEC_CP_CHALLENGE,
  ERROR_MSG_TIMEOUT_ERROR
} from '../../../constants';
import {
  ErrorDetails,
  LoginAuthWithCredentialsResponse,
  LoginOAuthResponse,
  OauthAEMConfig,
  PingFederateError,
  SwapAuthForAccessTokens
} from '../../../models';
import { ChallengeScreenError } from '../../../security-verification/challenge-success.service';
import { LoginRememberMeErrorExtendedUtilService } from './login-remember-me-error-extended-util.service';
@Injectable({
  providedIn: 'root'
})
export class LoginRememberMeErrorUtilService extends LoginRememberMeErrorExtendedUtilService {
  errorCode = '';

  checkRememberMeErrors(
    oAuthConfig: OauthAEMConfig,
    response:
      | LoginOAuthResponse
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
      | HttpErrorResponse
      | string
  ): string {
    if (response instanceof TimeoutError) {
      return ERROR_MSG_TIMEOUT_ERROR;
    } else if (response instanceof ChallengeScreenError) {
      return ERROR_MSG_SEC_CP_CHALLENGE;
    } else if (typeof response === 'string') {
      return response;
    } else {
      return this.validateErrorResponse(oAuthConfig, response);
    }
  }

  validateErrorResponse(
    oAuthConfig: OauthAEMConfig,
    response:
      | LoginOAuthResponse
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
      | HttpErrorResponse
  ) {
    if (this.checkHttpErrorResponse(oAuthConfig, response)) {
      this.errorCode = 'ERROR_MSG_EAM_OUTAGE';
    } else if (typeof response.error === 'string') {
      this.errorCode = `ERROR_MSG_${response.error}`.toUpperCase();
    } else if (typeof response.error?.error === 'string') {
      this.errorCode = `ERROR_MSG_${response.error.error}`.toUpperCase();
    } else {
      this.checkRememberMeErrorsHelper(response);
    }
    return this.errorCode;
  }

  checkHttpErrorResponse(
    oAuthConfig: OauthAEMConfig,
    response:
      | LoginOAuthResponse
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
      | HttpErrorResponse
  ): boolean {
    return (
      response instanceof HttpErrorResponse &&
      this.getMfaHttpError(oAuthConfig, response)
    );
  }

  getMfaHttpError(
    oauthConfig: OauthAEMConfig,
    response: HttpErrorResponse
  ): boolean {
    return !!(
      oauthConfig?.EAM_OUTAGE_ERROR_CODE_IGNORE &&
      oauthConfig.EAM_OUTAGE_ERROR_CODE_IGNORE.filter((code) => {
        return response?.status === parseInt(code);
      }).length < 1
    );
  }

  checkRememberMeErrorsHelper(
    response:
      | LoginOAuthResponse
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
  ): void {
    const pingError = response?.error as PingFederateError;
    if (pingError?.details && pingError?.details[0]?.code) {
      this.checkDetailsUserMessageErrors(pingError);
    } else {
      this.checkErrorCodes(response);
    }
  }

  checkErrorCodes(
    response:
      | LoginOAuthResponse
      | LoginAuthWithCredentialsResponse
      | SwapAuthForAccessTokens
  ): void {
    const error = response?.error as PingFederateError;
    const errorCode = error?.code ? error?.code : response?.code;
    this.errorCode = `ERROR_MSG_${errorCode}`.toUpperCase();
  }

  checkDetailsUserMessageErrors(pingError: PingFederateError): void {
    const pingErrorDetails: ErrorDetails =
      (pingError && pingError.details && pingError.details[0]) ??
      ({} as ErrorDetails);
    this.checkDetailsUserMessageErrorsHelper(pingErrorDetails);
  }

  checkDetailsUserMessageErrorsHelper(pingErrorDetails: ErrorDetails) {
    if (this.isCredentialValidationFailed(pingErrorDetails)) {
      this.checkPFCredentialValidationFailed(pingErrorDetails?.userMessage);
    } else if (this.checkPasswordChangeError(pingErrorDetails)) {
      this.errorCode = this.checkPasswordChangeErrorFailed(
        pingErrorDetails.userMessage
      );
    } else {
      this.errorCode = `ERROR_MSG_${pingErrorDetails?.code}`.toUpperCase();
    }
  }

  isCredentialValidationFailed(pingErrorDetails: ErrorDetails): boolean {
    return !!(
      pingErrorDetails &&
      pingErrorDetails.code === CREDENTIAL_VALIDATION_FAILED &&
      pingErrorDetails.userMessage
    );
  }

  checkPFCredentialValidationFailed(message?: string): void {
    const splitMessage = this.splitUserMessage(message ? message : '');
    this.errorCode = `ERROR_MSG_${splitMessage}`.toUpperCase();
  }
}
